type SkyGraphicVariant = 'horizon' | 'compass' | 'flightpath' | 'contour' | 'grid';

interface SkyGraphicProps {
  variant?: SkyGraphicVariant;
  tone?: 'dark' | 'light';
  className?: string;
}

const SkyGraphic = ({ variant = 'horizon', tone = 'dark', className = '' }: SkyGraphicProps) => {
  const isDark = tone === 'dark';
  const bg = isDark ? '#00103e' : '#eef3ff';
  const lineStrong = isDark ? 'rgba(248,192,0,0.55)' : 'rgba(0,40,144,0.45)';
  const lineSoft = isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,40,144,0.16)';
  const lineFaint = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,40,144,0.08)';
  const dot = isDark ? '#f8c000' : '#002890';

  return (
    <svg
      className={`absolute inset-0 w-full h-full ${className}`}
      viewBox="0 0 800 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <rect width="800" height="600" fill={bg} />

      {variant === 'horizon' && (
        <>
          <circle cx="640" cy="120" r="46" fill="none" stroke={lineStrong} strokeWidth="1.5" />
          <circle cx="640" cy="120" r="3" fill={dot} />
          <path d="M -40 340 Q 400 260 840 340" fill="none" stroke={lineSoft} strokeWidth="1.5" />
          <path d="M -40 400 Q 400 330 840 400" fill="none" stroke={lineFaint} strokeWidth="1.5" />
          <path d="M -40 460 Q 400 400 840 460" fill="none" stroke={lineFaint} strokeWidth="1" />
          <path d="M -40 520 Q 400 470 840 520" fill="none" stroke={lineFaint} strokeWidth="1" />
          {Array.from({ length: 9 }).map((_, i) => (
            <line key={i} x1={60 + i * 90} y1="340" x2={60 + i * 90} y2="352" stroke={lineSoft} strokeWidth="1" />
          ))}
        </>
      )}

      {variant === 'compass' && (
        <g transform="translate(560 300)">
          <circle r="180" fill="none" stroke={lineFaint} strokeWidth="1" />
          <circle r="130" fill="none" stroke={lineSoft} strokeWidth="1" />
          <circle r="80" fill="none" stroke={lineStrong} strokeWidth="1.5" />
          <circle r="3" fill={dot} />
          {Array.from({ length: 12 }).map((_, i) => (
            <line
              key={i}
              x1="0"
              y1="-180"
              x2="0"
              y2={i % 3 === 0 ? '-160' : '-172'}
              stroke={i % 3 === 0 ? lineStrong : lineSoft}
              strokeWidth={i % 3 === 0 ? 2 : 1}
              transform={`rotate(${i * 30})`}
            />
          ))}
        </g>
      )}

      {variant === 'flightpath' && (
        <>
          <path
            d="M -40 480 Q 260 200 460 260 T 840 100"
            fill="none"
            stroke={lineStrong}
            strokeWidth="1.5"
            strokeDasharray="2 10"
            strokeLinecap="round"
          />
          <circle cx="260" cy="220" r="4" fill="none" stroke={lineSoft} strokeWidth="1.5" />
          <circle cx="460" cy="260" r="4" fill="none" stroke={lineSoft} strokeWidth="1.5" />
          <circle cx="660" cy="150" r="4" fill="none" stroke={lineSoft} strokeWidth="1.5" />
          <path d="M 820 88 L 840 100 L 820 112 L 826 100 Z" fill={dot} />
        </>
      )}

      {variant === 'contour' && (
        <>
          {[220, 170, 125, 85, 50].map((r, i) => (
            <ellipse
              key={r}
              cx={420 + i * 6}
              cy={310 - i * 4}
              rx={r * 1.4}
              ry={r}
              fill="none"
              stroke={i === 2 ? lineStrong : lineFaint}
              strokeWidth={i === 2 ? 1.5 : 1}
            />
          ))}
          <circle cx="420" cy="310" r="3" fill={dot} />
        </>
      )}

      {variant === 'grid' && (
        <>
          {Array.from({ length: 7 }).map((_, i) => {
            const y = 360 + i * i * 6;
            return <line key={`h${i}`} x1="-40" y1={y} x2="840" y2={y} stroke={lineFaint} strokeWidth="1" />;
          })}
          {Array.from({ length: 9 }).map((_, i) => {
            const xTop = 100 + i * 75;
            const xBottom = 400 + (xTop - 400) * 3;
            return <line key={`v${i}`} x1={xTop} y1="360" x2={xBottom} y2="600" stroke={lineFaint} strokeWidth="1" />;
          })}
          <line x1="-40" y1="360" x2="840" y2="360" stroke={lineStrong} strokeWidth="1.5" />
        </>
      )}
    </svg>
  );
};

export default SkyGraphic;
